import type RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { stepArcadeVehicle, type DriveTelemetry } from '../physics/arcade';
import type { DriveInput } from '../input/Input';
import { SURFACES, type SurfaceId, type SurfaceParams } from '../physics/surfaces';
import type { ColliderMeta } from '../tracks/types';
import { CART, CartView } from './CartModel';

export interface GroundProbe {
  grounded: boolean;
  onTrack: boolean;
  surface: SurfaceParams;
  toi: number;
  kind: ColliderMeta['kind'] | 'none';
}

export class Vehicle {
  readonly view: CartView;
  readonly body: RAPIER.RigidBody;
  readonly collider: RAPIER.Collider;
  splineHint = 0;
  splineDistance = 0;
  previousPosition = new THREE.Vector3();
  currentPosition = new THREE.Vector3();
  previousQuaternion = new THREE.Quaternion();
  currentQuaternion = new THREE.Quaternion();
  telemetry: DriveTelemetry = { forwardSpeed: 0, lateralSpeed: 0, grounded: false };
  probe: GroundProbe = {
    grounded: false,
    onTrack: true,
    surface: SURFACES.asphalt,
    toi: CART.rideHeight,
    kind: 'road',
  };
  private readonly ray: RAPIER.Ray;

  constructor(
    rapier: typeof RAPIER,
    private readonly world: RAPIER.World,
    private readonly meta: Map<number, ColliderMeta>,
    view: CartView,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    defaultSurface: SurfaceId,
  ) {
    this.view = view;
    this.body = world.createRigidBody(
      rapier.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.06)
        .setAngularDamping(2.4)
        .setCanSleep(false)
        .setCcdEnabled(true)
        .setSoftCcdPrediction(0.2)
        .setAdditionalSolverIterations(3)
        .setAdditionalMassProperties(
          CART.mass,
          { x: 0, y: -0.18, z: 0 },
          { x: 9, y: 14, z: 7 },
          { x: 0, y: 0, z: 0, w: 1 },
        ),
    );
    const { hx, hy, hz, y, z } = CART.collider;
    this.collider = world.createCollider(
      rapier.ColliderDesc.cuboid(hx, hy, hz)
        .setTranslation(0, y, z)
        .setDensity(0)
        .setFriction(0.05)
        .setRestitution(0),
      this.body,
    );
    this.meta.set(this.collider.handle, { kind: 'vehicle', surface: defaultSurface });
    this.ray = new rapier.Ray({ x: 0, y: 0, z: 0 }, { x: 0, y: -1, z: 0 });
    this.previousPosition.copy(position);
    this.currentPosition.copy(position);
    this.previousQuaternion.copy(quaternion);
    this.currentQuaternion.copy(quaternion);
    this.view.setPose(position, quaternion);
    this.probe.surface = SURFACES[defaultSurface];
  }

  capturePose(): void {
    this.previousPosition.copy(this.currentPosition);
    this.previousQuaternion.copy(this.currentQuaternion);
    const translation = this.body.translation();
    const rotation = this.body.rotation();
    this.currentPosition.set(translation.x, translation.y, translation.z);
    this.currentQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  }

  teleport(position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.body.setTranslation({ x: position.x, y: position.y, z: position.z }, true);
    this.body.setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.previousPosition.copy(position);
    this.currentPosition.copy(position);
    this.previousQuaternion.copy(quaternion);
    this.currentQuaternion.copy(quaternion);
    this.view.setPose(position, quaternion);
    this.world.propagateModifiedBodyPositionsToColliders();
  }

  probeGround(defaultSurface: SurfaceId): GroundProbe {
    const translation = this.body.translation();
    this.ray.origin.x = translation.x;
    this.ray.origin.y = translation.y + 0.05;
    this.ray.origin.z = translation.z;
    const hit = this.world.castRay(this.ray, 3, true, undefined, undefined, undefined, this.body);
    if (!hit) {
      this.probe = {
        grounded: false,
        onTrack: false,
        surface: SURFACES.offroad,
        toi: 99,
        kind: 'none',
      };
      return this.probe;
    }
    const info = this.meta.get(hit.collider.handle);
    const kind = info?.kind ?? 'none';
    const surface = SURFACES[info?.surface ?? defaultSurface] ?? SURFACES[defaultSurface];
    const grounded = hit.timeOfImpact < 1.35 && (kind === 'road' || kind === 'ground' || kind === 'obstacle');
    const onTrack = (kind === 'road' || kind === 'barrier' || kind === 'obstacle') && hit.timeOfImpact < 2.4;
    this.probe = { grounded, onTrack, surface, toi: hit.timeOfImpact, kind };
    return this.probe;
  }

  /** Keeps the cart on the road height so segment seams do not fight the arcade drive. */
  pinToRoad(y: number): void {
    const translation = this.body.translation();
    const velocity = this.body.linvel();
    this.body.setTranslation({ x: translation.x, y, z: translation.z }, true);
    this.body.setLinvel({ x: velocity.x, y: 0, z: velocity.z }, true);
    this.body.setGravityScale(0, true);
  }

  releaseRoad(): void {
    this.body.setGravityScale(1, true);
  }

  step(input: DriveInput, dt: number, defaultSurface: SurfaceId, pinned = false): void {
    const probe = this.probeGround(defaultSurface);
    const onRoad = probe.kind === 'road' || probe.kind === 'obstacle' || probe.kind === 'barrier';
    const surface = onRoad ? probe.surface : pinned ? SURFACES[defaultSurface] : SURFACES.offroad;
    this.telemetry = stepArcadeVehicle(this.body, input, surface, pinned || probe.grounded, dt);
    if (pinned && !onRoad) this.probe = { ...this.probe, grounded: true, onTrack: true, surface };
  }

  render(alpha: number, dt: number, steerAngle: number, braking: boolean, rough = false, frontBounce = false, boosting = false): void {
    const position = this.previousPosition.clone().lerp(this.currentPosition, alpha);
    const quaternion = this.previousQuaternion.clone().slerp(this.currentQuaternion, alpha);
    this.view.setPose(position, quaternion);
    this.view.update(
      dt,
      steerAngle,
      this.telemetry.forwardSpeed,
      this.telemetry.lateralSpeed,
      braking,
      !this.probe.grounded,
      rough,
      frontBounce,
      boosting,
    );
  }

  dispose(): void {
    this.meta.delete(this.collider.handle);
    this.world.removeRigidBody(this.body);
    this.view.root.removeFromParent();
    this.view.dispose();
  }
}
